import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  HStack,
  Text,
  Badge,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Stack,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Code,
} from '@chakra-ui/react';
import type { ComparisonData } from './types';

type ComparisonModalProps = {
  isOpen: boolean;
  onClose: () => void;
  billableId: string;
  onCompare: (billableId: string) => void;
  comparisonData: ComparisonData | null | undefined;
  isLoading: boolean;
};

export const ComparisonModal: React.FC<ComparisonModalProps> = React.memo(
  ({ isOpen, onClose, billableId, onCompare, comparisonData, isLoading }) => {
    const prevIsOpenRef = React.useRef(false);

    React.useEffect(() => {
      // Only call onCompare when modal transitions from closed to open
      if (isOpen && !prevIsOpenRef.current && billableId) {
        onCompare(billableId);
      }
      prevIsOpenRef.current = isOpen;
    }, [isOpen, billableId, onCompare]);

    const renderValue = (value: unknown): string => {
      if (value == null) {
        return '—';
      }
      if (typeof value === 'string') {
        return value;
      }
      if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
        return String(value);
      }
      return JSON.stringify(value);
    };

    return (
      <Modal isOpen={isOpen} onClose={onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack justify="space-between">
              <Text>Xero Comparison</Text>
              {comparisonData?.ok === true && comparisonData.matches !== undefined && (
                <Badge colorScheme={comparisonData.matches ? 'green' : 'orange'} mr={10}>
                  {comparisonData.matches ? 'Match' : 'Differences Found'}
                </Badge>
              )}
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflow="hidden">
            {isLoading ? (
              <Flex justify="center" align="center" minH="200px">
                <Spinner size="lg" />
              </Flex>
            ) : comparisonData?.ok === false ? (
              <Alert status="error">
                <AlertIcon />
                {comparisonData.error ?? 'An error occurred'}
              </Alert>
            ) : comparisonData?.ok === true ? (
              <Stack spacing={4}>
                <Box>
                  <Text fontSize="sm" color="gray.500" mb={2}>
                    Billable ID: {comparisonData.billableId}
                  </Text>
                  <Text fontSize="sm" color="gray.500" mb={4}>
                    Xero Invoice ID: {comparisonData.xeroInvoiceId ?? '—'}
                  </Text>
                </Box>

                <Box
                  overflowX="auto"
                  overflowY="auto"
                  maxH="500px"
                  sx={{
                    '&::-webkit-scrollbar': { width: '8px', height: '8px' },
                    '&::-webkit-scrollbar-track': { bg: 'gray.100' },
                    '&::-webkit-scrollbar-thumb': { bg: 'gray.400', borderRadius: 'md' },
                  }}
                >
                  <Table variant="simple" size="sm" style={{ tableLayout: 'fixed' }} minW="800px">
                    <Thead
                      position="sticky"
                      top={0}
                      bg="bg.surface"
                      zIndex={1}
                      _dark={{ bg: 'gray.800' }}
                    >
                      <Tr>
                        <Th width="150px">Field</Th>
                        <Th width="40%">Database</Th>
                        <Th width="40%">Xero</Th>
                        <Th width="100px">Status</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {comparisonData.diffs?.map((diff) => (
                        <Tr
                          key={diff.field}
                          bg={diff.match ? undefined : 'orange.50'}
                          _dark={{ bg: diff.match ? undefined : 'orange.900' }}
                        >
                          <Td
                            fontWeight={diff.match ? 'normal' : 'semibold'}
                            whiteSpace="normal"
                            width="150px"
                          >
                            {diff.field}
                          </Td>
                          <Td whiteSpace="normal" width="40%">
                            <Code
                              fontSize="xs"
                              whiteSpace="pre-wrap"
                              wordBreak="break-word"
                              display="block"
                            >
                              {renderValue(diff.db)}
                            </Code>
                          </Td>
                          <Td whiteSpace="normal" width="40%">
                            <Code
                              fontSize="xs"
                              whiteSpace="pre-wrap"
                              wordBreak="break-word"
                              display="block"
                            >
                              {renderValue(diff.xero)}
                            </Code>
                          </Td>
                          <Td width="100px">
                            <Badge colorScheme={diff.match ? 'green' : 'red'}>
                              {diff.match ? '✓' : '✗'}
                            </Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Stack>
            ) : (
              <Text color="gray.500">No comparison data available</Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  },
);
ComparisonModal.displayName = 'ComparisonModal';
